import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { SxProps, Theme } from '@mui/material/styles';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

export interface OfflineIndicatorProps {
  /**
   * Position of the indicator on screen. Defaults to bottom-left so it doesn't
   * overlap with `ToastContainer` (top-right) or floating action buttons.
   */
  anchor?: 'bottom-left' | 'bottom-center' | 'bottom-right';
  /** How long the "back online" confirmation stays visible, in ms. */
  backOnlineDurationMs?: number;
  sx?: SxProps<Theme>;
}

const anchorOriginByAnchor: Record<
  NonNullable<OfflineIndicatorProps['anchor']>,
  {
    vertical: 'bottom';
    horizontal: 'left' | 'center' | 'right';
  }
> = {
  'bottom-left': { vertical: 'bottom', horizontal: 'left' },
  'bottom-center': { vertical: 'bottom', horizontal: 'center' },
  'bottom-right': { vertical: 'bottom', horizontal: 'right' },
};

/**
 * Discreet, always-mounted indicator that reacts to the browser's online/offline
 * events (see `useOnlineStatus`).
 *
 * - When the user goes offline a persistent Snackbar appears at the requested
 *   anchor with a `warning` alert.
 * - When they come back online, the alert briefly switches to a `success`
 *   confirmation and auto-hides after `backOnlineDurationMs`.
 *
 * Introduced together with the Apollo retry link / monitoring offline filter
 * (TEC-1684) so users get a visible cue when their queries silently fail.
 */
export const OfflineIndicator = ({
  anchor = 'bottom-left',
  backOnlineDurationMs = 4000,
  sx,
}: OfflineIndicatorProps = {}) => {
  const { t } = useTranslation(['network']);
  const isOnline = useOnlineStatus();
  const wasOfflineRef = useRef(false);
  const [showBackOnline, setShowBackOnline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
      setShowBackOnline(false);
      return;
    }
    if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      setShowBackOnline(true);
    }
  }, [isOnline]);

  const anchorOrigin = anchorOriginByAnchor[anchor];
  const open = !isOnline || showBackOnline;

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setShowBackOnline(false);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={anchorOrigin}
      autoHideDuration={isOnline ? backOnlineDurationMs : null}
      onClose={handleClose}
      sx={{ maxWidth: 360, ...sx }}
    >
      {isOnline ? (
        <Alert
          severity="success"
          icon={<CloudDoneIcon fontSize="small" />}
          variant="filled"
          onClose={() => setShowBackOnline(false)}
          sx={{ fontSize: 13 }}
        >
          {t('backOnline')}
        </Alert>
      ) : (
        <Alert severity="warning" icon={<CloudOffIcon fontSize="small" />} variant="filled" sx={{ fontSize: 13 }}>
          {t('offline')}
        </Alert>
      )}
    </Snackbar>
  );
};

export default OfflineIndicator;
